import { useId, useRef } from "react";
import { BobParticles } from "./BobParticles";
import { BobWaveShapes } from "./BobWaveShapes";
import design from "./bob-design.json";
import { useBobMotion } from "./useBobMotion";
import "./bob.css";

export type BobState = "idle" | "listening" | "thinking" | "synthesizing" | "formatting";
export type BobSize = "mini" | "compact" | "hero";

type BobProps = {
  readonly variant?: "symbol" | "wordmark" | "activity";
  readonly size?: BobSize;
  readonly state?: BobState;
  readonly className?: string;
  readonly label?: string;
  readonly narrative?: string;
  readonly decorative?: boolean;
  readonly onClick?: (() => void) | undefined;
};

const narratives: Record<BobState, string> = {
  idle: "Bob is ready.",
  listening: "Bob is listening.",
  thinking: "Bob is thinking…",
  synthesizing: "Bob is preparing the assessment and plan…",
  formatting: "Bob is formatting…",
};

/** The approved mark, wordmark and live assistant share one geometry and material. */
export function Bob({
  variant = "symbol",
  size = "compact",
  state = "idle",
  className = "",
  label,
  narrative,
  decorative = false,
  onClick,
}: BobProps) {
  const paintId = `bob-${useId()}`;
  const symbolRef = useRef<HTMLSpanElement>(null);
  const animated = variant === "activity";
  // Keep monitoring through the cosmetic return to idle. Workflow status and
  // aria-busy still update immediately when the actual work ends.
  const motion = useBobMotion(symbolRef, animated);
  const busy = state === "thinking" || state === "synthesizing" || state === "formatting";
  const classes =
    `bob bob-${variant} bob-size-${size} ${animated ? `state-${state}${motion ? "" : " bob-paused"}` : ""} ${className}`.trim();

  if (!animated) {
    const wordmark = variant === "wordmark";
    const mark = <BobWaveShapes id={paintId} />;
    return (
      <svg
        className={classes}
        viewBox={wordmark ? design.wordmark.viewBox : design.symbolViewBox}
        role={decorative ? undefined : "img"}
        aria-label={decorative ? undefined : (label ?? (wordmark ? "Bob Doc" : "Bob"))}
        aria-hidden={decorative || undefined}
        focusable="false"
      >
        {wordmark ? (
          <>
            {design.wordmark.letters.map((letter) => (
              <path
                key={letter.name}
                d={letter.path}
                transform={letter.transform}
                fill={letter.tone === "primary" ? design.primary : design.secondary}
                fillRule="evenodd"
              />
            ))}
            <g transform={design.wordmark.symbolTransform}>{mark}</g>
          </>
        ) : (
          mark
        )}
      </svg>
    );
  }

  const visual = (
    <span className="bob-swarm-boundary" aria-hidden="true">
      <span ref={symbolRef} className="bob-wave-symbol">
        <svg viewBox={design.symbolViewBox} focusable="false" aria-hidden="true">
          <BobWaveShapes id={paintId} />
        </svg>
      </span>
      <BobParticles
        symbolRef={symbolRef}
        thinking={busy}
        motionAllowed={motion && size !== "mini"}
      />
    </span>
  );
  const description = narrative ?? narratives[state];
  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        aria-label={label ?? "Open Bob assistant"}
        aria-haspopup="dialog"
        aria-busy={busy}
        onClick={onClick}
      >
        {visual}
        <span className="bob-sr" role="status" aria-live="polite">
          {description}
        </span>
      </button>
    );
  }
  if (decorative)
    return (
      <div className={classes} aria-hidden="true">
        {visual}
      </div>
    );
  return (
    <div className={classes} role="status" aria-label={label ?? "Bob status"} aria-busy={busy}>
      {visual}
      <span className="bob-sr">{description}</span>
    </div>
  );
}
